// Canvas assigned areas — shared data layer (Oct 19, 2026; daily areas Oct 23).
//
// One source for the home "My areas" widget, the Today tab's per-person area
// dropdown, the Today tab's list of every assigned area, the Today map and the
// Areas tab. All numbers come from the canvas_area_stats() RPC (SECURITY
// DEFINER, see supabase/migrations/20261019_canvas_area_stats.sql and
// 20261023_canvas_daily_areas.sql):
//
//   • pins = every knock pin INSIDE the area's polygon right now — not the
//     pin_ids snapshot stored when the lasso was drawn (that one goes stale:
//     new drops are missed, deleted pins linger).
//   • knock / talked / appt match the rep's own Canvas day-stats tiles:
//     any visit / contact_made·appt_set·follow_up·rent / appt_set.
//   • managers (canvas_can_manage) read any area; anyone else only their own.
//
// Daily areas (Oct 23): an area may carry `for_date` (the day it is for) and
// an anchor (the deal whose build or adjuster meeting it was drawn around).
// A standing area has neither — that is every area drawn before this change.
//
// Writes: add / remove a person go through canvas_area_add_member /
// canvas_area_remove_member; expire / carry through canvas_area_expire /
// canvas_area_carry (all RLS = canvas_can_manage); a rename is a direct
// update of the area's active rows, like Mark reviewed on the audit board.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "./supabase";
import { drawAreaSearch, type AnchorKind, type DayStop, type DrawAreaOptions, type ReviewArea } from "./dailyAreas";
export type { DrawAreaOptions } from "./dailyAreas";

export interface AreaRepStat {
  assignment_id: string;
  user_id: string;
  full_name: string;
  role: string | null;
  assigned_at: string;
  knocks_today: number;
  knocks_7d: number;
  knocks_since: number;
  talked_today: number;
  talked_7d: number;
  talked_since: number;
  appts_7d: number;
  appts_since: number;
  last_knock_at: string | null;
}

export interface AreaActivity {
  knocks_today: number;
  knocks_7d: number;
  knocks_30d: number;
  knocks_since: number;
  talked_today: number;
  talked_7d: number;
  talked_30d: number;
  talked_since: number;
  appts_today: number;
  appts_7d: number;
  appts_30d: number;
  appts_since: number;
  pins_knocked_since: number;
  last_knock_at: string | null;
  last_knock_by_name: string | null;
  others_knocks_7d: number;
  others_knocks_since: number;
}

export interface AreaHail {
  max_in: number;
  max_date: string;  // YYYY-MM-DD (Eastern)
  last_date: string; // YYYY-MM-DD (Eastern)
  days: number;      // distinct storm days with ≥1″ in the last year
}

/** Where a daily area was drawn around — street and city only, never the homeowner. */
export interface AreaAnchor {
  street: string | null;
  city: string | null;
  zip: string | null;
  lat: number | null;
  lon: number | null;
  /** "8:00 am" for a meeting, "Oct 27" for a build. */
  when: string | null;
}

export interface AreaStats {
  area_group_id: string;
  area_label: string | null;
  notes: string | null;
  share_mode: string | null;
  storm_tied: boolean;
  first_assigned_at: string;
  last_reviewed_at: string | null;
  /** YYYY-MM-DD for a daily area; null for a standing area. */
  for_date: string | null;
  anchor_kind: AnchorKind | null;
  anchor_deal_id: string | null;
  anchor: AreaAnchor | null;
  center: { lat: number; lon: number };
  /** [minLon, minLat, maxLon, maxLat] */
  bbox: [number, number, number, number];
  sq_mi: number;
  pin_count: number;
  states: Record<string, number>;
  cities: Array<{ city: string; n: number }>;
  zips: Array<{ zip: string; n: number }>;
  activity: AreaActivity;
  /** Knocks per Eastern day, last 14 days, oldest first. */
  daily: Array<{ d: string; n: number }>;
  deals: { deals: number; claims: number; builds: number; revenue: number };
  hail: AreaHail | null;
  reps: AreaRepStat[];
}

// ── Reads ────────────────────────────────────────────────────────────────

export async function fetchAreaStats(opts: { groupIds?: string[] | null; userId?: string | null } = {}): Promise<AreaStats[]> {
  const { data, error } = await supabase.rpc("canvas_area_stats", {
    p_group_ids: opts.groupIds && opts.groupIds.length ? opts.groupIds : null,
    p_user: opts.userId ?? null,
  });
  if (error) throw new Error(error.message);
  return (Array.isArray(data) ? data : []) as AreaStats[];
}

/**
 * Live area stats with realtime refresh. `userId` narrows to the areas that
 * person is on (the home widget); omit it for every area the viewer may see
 * (the Today tab).
 *
 * Refresh is a throttle, not a debounce — at most one refetch every 5 seconds
 * however busy the field is (a debounce would starve while knocks keep
 * coming) — and a hidden tab waits until it's looked at again. Knocks only
 * matter to someone who has an area, so the canvas_visits feed is subscribed
 * only then; everyone listens for assignment changes (that's how a rep's
 * widget appears the moment a manager adds them).
 */
export function useAreaStats(opts: { userId?: string | null; enabled?: boolean } = {}) {
  const { userId = null, enabled = true } = opts;
  const [areas, setAreas] = useState<AreaStats[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const rows = await fetchAreaStats({ userId });
      if (mine !== seq.current) return; // a newer load superseded this one
      setAreas(rows);
      setError(null);
    } catch (e) {
      if (mine !== seq.current) return;
      setError((e as Error).message || "Couldn't load areas.");
    }
  }, [userId]);

  useEffect(() => {
    if (!enabled) return;
    void reload();
  }, [enabled, reload]);

  const watchKnocks = !!areas && areas.length > 0;
  useEffect(() => {
    if (!enabled) return;
    let t: ReturnType<typeof setTimeout> | null = null;
    let stale = false;
    const fire = () => {
      t = null;
      if (document.visibilityState === "hidden") { stale = true; return; }
      void reload();
    };
    const bump = () => { if (!t) t = setTimeout(fire, 5000); };
    const onVisible = () => {
      if (document.visibilityState === "visible" && stale) { stale = false; void reload(); }
    };
    document.addEventListener("visibilitychange", onVisible);
    let ch = supabase
      .channel(`area-stats-${userId ?? "all"}-${Math.random().toString(36).slice(2, 8)}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "canvas_area_assignments" }, bump);
    if (watchKnocks) {
      ch = ch.on("postgres_changes", { event: "INSERT", schema: "public", table: "canvas_visits" }, bump);
    }
    ch.subscribe();
    return () => {
      if (t) clearTimeout(t);
      document.removeEventListener("visibilitychange", onVisible);
      void supabase.removeChannel(ch);
    };
  }, [enabled, reload, userId, watchKnocks]);

  return { areas, error, reload };
}

/** Active area outlines by area_group_id (the same RPC the canvas map draws from). */
export interface AreaOutline {
  area_group_id: string;
  geometry: unknown;
  for_date: string | null;
  anchor_kind: AnchorKind | null;
  area_label: string | null;
}

export async function fetchAreaOutlines(): Promise<Map<string, AreaOutline>> {
  const { data, error } = await supabase.rpc("get_active_canvas_areas_geojson");
  if (error) throw new Error(error.message);
  const m = new Map<string, AreaOutline>();
  type Feat = { geometry?: unknown; properties?: { area_group_id?: string; for_date?: string | null; anchor_kind?: AnchorKind | null; area_label?: string | null } };
  for (const f of ((data as { features?: Feat[] })?.features ?? [])) {
    const gid = f?.properties?.area_group_id;
    if (gid && f.geometry && !m.has(gid)) {
      m.set(gid, {
        area_group_id: gid, geometry: f.geometry,
        for_date: f.properties?.for_date ?? null,
        anchor_kind: f.properties?.anchor_kind ?? null,
        area_label: f.properties?.area_label ?? null,
      });
    }
  }
  return m;
}

/** Builds, adjuster meetings and reinspections for a day (or a span of days). Managers see everyone's; a rep only their own. */
export async function fetchDayStops(from: string, to?: string): Promise<DayStop[]> {
  const { data, error } = await supabase.rpc("canvas_day_stops", { p_from: from, p_to: to ?? null });
  if (error) throw new Error(error.message);
  const rows = (Array.isArray(data) ? data : []) as Array<Omit<DayStop, "lat" | "lon"> & { lat: unknown; lon: unknown }>;
  return rows.map((r) => ({
    ...r,
    lat: r.lat == null ? null : Number(r.lat),
    lon: r.lon == null ? null : Number(r.lon),
  }));
}

/** Every area active in [from, to] with knocks by day and by person — the week / month review. */
export async function fetchAreaReview(from: string, to: string): Promise<ReviewArea[]> {
  const { data, error } = await supabase.rpc("canvas_area_review", { p_from: from, p_to: to });
  if (error) throw new Error(error.message);
  return (Array.isArray(data) ? data : []) as ReviewArea[];
}

// ── Writes ───────────────────────────────────────────────────────────────

export async function addAreaMember(groupId: string, memberId: string): Promise<{ already: boolean }> {
  const { data, error } = await supabase.rpc("canvas_area_add_member", { p_group_id: groupId, p_member_id: memberId });
  if (error) throw new Error(error.message);
  return { already: !!(data as { already?: boolean } | null)?.already };
}

export async function removeAreaMember(groupId: string, memberId: string): Promise<{ areaStillActive: boolean }> {
  const { data, error } = await supabase.rpc("canvas_area_remove_member", { p_group_id: groupId, p_member_id: memberId });
  if (error) throw new Error(error.message);
  return { areaStillActive: !!(data as { area_still_active?: boolean } | null)?.area_still_active };
}

export async function renameArea(groupId: string, label: string): Promise<void> {
  const { error } = await supabase
    .from("canvas_area_assignments")
    .update({ area_label: label.trim() || null })
    .eq("area_group_id", groupId)
    .eq("status", "active");
  if (error) throw new Error(error.message);
}

/** Ends an area for everyone on it (daily "Expire" and standing "Remove area" are the same write). */
export async function expireArea(groupId: string): Promise<{ removed: number }> {
  const { data, error } = await supabase.rpc("canvas_area_expire", { p_group_id: groupId });
  if (error) throw new Error(error.message);
  return { removed: Number((data as { removed?: number } | null)?.removed ?? 0) };
}

/** Same polygon and people as a new daily area for `forDate`; the old area ends. */
export async function carryArea(groupId: string, forDate: string): Promise<{ newGroupId: string; reps: number }> {
  const { data, error } = await supabase.rpc("canvas_area_carry", { p_group_id: groupId, p_for_date: forDate });
  if (error) throw new Error(error.message);
  const d = (data ?? {}) as { new_group_id?: string; reps?: number };
  return { newGroupId: String(d.new_group_id ?? ""), reps: Number(d.reps ?? 0) };
}

// ── Display helpers ──────────────────────────────────────────────────────

/** "Kennesaw · 30152" — top city, then up to two ZIPs. */
export function areaPlace(a: Pick<AreaStats, "cities" | "zips">): string {
  const cities = a.cities.slice(0, 2).map((c) => c.city);
  const zips = a.zips.slice(0, 2).map((z) => z.zip);
  return [cities.join(" / "), zips.join(", ")].filter(Boolean).join(" · ");
}

/** The name managers gave the area, else where it was drawn around, else where it is. */
export function areaName(a: Pick<AreaStats, "area_label" | "cities" | "zips"> & { anchor?: AreaAnchor | null }): string {
  if (a.area_label?.trim()) return a.area_label.trim();
  if (a.anchor && (a.anchor.city || a.anchor.street)) {
    const st = (a.anchor.street ?? "").trim().replace(/^\d+[a-z]?(-\d+)?\s+/i, "").replace(/\s+(N|S|E|W|NE|NW|SE|SW)\.?$/i, "");
    const n = [a.anchor.city?.trim(), st].filter(Boolean).join(" · ");
    if (n) return n;
  }
  return areaPlace(a) || "Unnamed area";
}

export interface PinGroup {
  key: string;
  label: string;
  states: string[];
  /** Matches the knock-pin colors on the canvas map. */
  color: string;
}

export const PIN_GROUPS: PinGroup[] = [
  { key: "open",      label: "Not knocked", states: ["open"],                                     color: "#f97316" },
  { key: "no_answer", label: "No answer",   states: ["knocked", "not_home_3x"],                   color: "#eab308" },
  // "Contacted" (the pin's state), not "talked" — "talked" is the activity count.
  { key: "contacted", label: "Contacted",   states: ["contacted", "rent"],                        color: "#a855f7" },
  { key: "pipeline",  label: "Appt / claim", states: ["appt_scheduled", "lead", "claim_called"],  color: "#06b6d4" },
  { key: "customers", label: "Customers",   states: ["built", "legacy_client"],                  color: "#22c55e" },
  { key: "dnk",       label: "Don't knock", states: ["do_not_knock", "wrong_address"],           color: "#94a3b8" },
];

export function groupStates(states: Record<string, number>): Array<PinGroup & { n: number }> {
  const known = new Set(PIN_GROUPS.flatMap((g) => g.states));
  const out = PIN_GROUPS.map((g) => ({ ...g, n: g.states.reduce((s, k) => s + (states[k] ?? 0), 0) }));
  const other = Object.entries(states).filter(([k]) => !known.has(k)).reduce((s, [, n]) => s + n, 0);
  if (other > 0) out.push({ key: "other", label: "Other", states: [], color: "#cbd5e1", n: other });
  return out;
}

/** Doors still worth a knock: never knocked, or knocked with no answer yet. */
export function doorsLeft(states: Record<string, number>): number {
  return (states.open ?? 0) + (states.knocked ?? 0);
}

/** Share of the area's pins knocked at least once since it was assigned (0–1). */
export function coverage(a: Pick<AreaStats, "pin_count" | "activity">): number {
  if (!a.pin_count) return 0;
  return Math.min(1, a.activity.pins_knocked_since / a.pin_count);
}

export function relTime(iso: string | null | undefined): string {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  const d = Math.floor(ms / 86_400_000);
  return d === 1 ? "yesterday" : `${d}d ago`;
}

/** "Aug 9" for a YYYY-MM-DD (date-only — noon UTC so no timezone slip). */
export function fmtShortDate(day: string | null | undefined): string {
  if (!day) return "—";
  const d = new Date(String(day).slice(0, 10) + "T12:00:00Z");
  return d.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" });
}

/** "Oct 13" for a timestamp, in Eastern time. */
export function fmtSince(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric" });
}

export function firstName(full: string | null | undefined): string {
  return (full ?? "").trim().split(/\s+/)[0] || "—";
}

/** Opens the canvas map fitted to this area. */
export function areaMapHref(groupId: string): string {
  return `/canvas?area=${encodeURIComponent(groupId)}`;
}

/**
 * Opens the canvas map in draw-an-area mode with this person pre-picked —
 * and, from the Today tab, landed on their stop with the day and name set.
 */
export function drawAreaHref(memberId: string, opts: DrawAreaOptions = {}): string {
  return `/canvas?${drawAreaSearch(memberId, opts)}`;
}
